import React from 'react';
import { Link, useParams } from 'react-router-dom';
import MoreButtonArticle from '../MoreButton/MoreButtonArticle';
import { useEffect, useState, useCallback, useMemo } from "react";
import Discount from './PromoDiscount/Discount';
import FAQs from '../FAQsSection/FAQs';
import ErrorPage from '../../pages/404Page';
import SEO from '../SEO/SEO';
import { useTranslation } from 'react-i18next';
import PromoPageComponentSkeleton from './PromoPageComponentSkeleton';
import { useCachedFetch } from './../../hooks/useCachedFetch';
import { IMAGES_BASE_URL } from '../../config/constants';
import "./PromoPageComponent.scss";

const getTimeUnitLabel = (value, unit, language) => {
    const pluralRules = {
        ua: {
            days: ['день', 'дні', 'днів'],
            hours: ['година', 'години', 'годин'],
            minutes: ['хвилина', 'хвилини', 'хвилин'],
            seconds: ['секунда', 'секунди', 'секунд']
        },
        en: {
            days: ['day', 'days', 'days'],
            hours: ['hour', 'hours', 'hours'],
            minutes: ['minute', 'minutes', 'minutes'],
            seconds: ['second', 'seconds', 'seconds']
        },
        ru: {
            days: ['день', 'дня', 'дней'],
            hours: ['час', 'часа', 'часов'],
            minutes: ['минута', 'минуты', 'минут'],
            seconds: ['секунда', 'секунды', 'секунд']
        },
        pl: {
            days: ['dzień', 'dni', 'dni'],
            hours: ['godzina', 'godziny', 'godzin'],
            minutes: ['minuta', 'minuty', 'minut'],
            seconds: ['sekunda', 'sekundy', 'sekund']
        },
        de: {
            days: ['Tag', 'Tage', 'Tagen'],
            hours: ['Stunde', 'Stunden', 'Stunden'],
            minutes: ['Minute', 'Minuten', 'Minuten'],
            seconds: ['Sekunde', 'Sekunden', 'Sekunden']
        },
        es: {
            days: ['día', 'días', 'días'],
            hours: ['hora', 'horas', 'horas'],
            minutes: ['minuto', 'minutos', 'minutos'],
            seconds: ['segundo', 'segundos', 'segundos']
        }
    };

    const rules = pluralRules[language] || pluralRules.en;
    const forms = rules[unit] || rules.days;

    if (['en', 'de', 'es'].includes(language)) {
        return value === 1 ? forms[0] : forms[1];
    }
    else if (language === 'pl') {
        if (value === 1) return forms[0];
        const lastDigit = value % 10;
        const lastTwoDigits = value % 100;

        if ((lastDigit >= 2 && lastDigit <= 4) && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)) {
            return forms[1];
        }
        return forms[2];
    }
    else {
        if (value % 10 === 1 && value % 100 !== 11) {
            return forms[0];
        } else if ([2, 3, 4].includes(value % 10) && ![12, 13, 14].includes(value % 100)) {
            return forms[1];
        } else {
            return forms[2];
        }
    }
};

const PromoPageComponent = ({ hideHeaderFooterSetter, hideLanguageSwitcherSetter }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const { slug } = useParams();

    const { data: promoJson, loading: dataLoading, error: dataError } = useCachedFetch('/data/locales/promo.json');

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);

    useEffect(() => {
        if (hideLanguageSwitcherSetter) hideLanguageSwitcherSetter(true);
        
        return () => {
            if (hideLanguageSwitcherSetter) hideLanguageSwitcherSetter(false);
        };
    }, [ hideLanguageSwitcherSetter]);

    useEffect(() => {
        if (dataLoading) return;

        if (dataError) {
            setHasError(true);
            setIsLoading(false);
            return;
        }

        const data = promoJson?.comingSoonData;

        if (data && data.length > 0) {
            const promoItem = data.find(item => item.slug === slug);
            
            if (!promoItem) {
                setHasError(true);
            } else {
                const translation = promoItem.translations?.[currentLang];

                const isActive = translation?.isActive === true;
                
                if (!isActive) {
                    setHasError(true);
                }
            }
        } else {
            setHasError(true);
        }

        setIsLoading(false);
    }, [dataLoading, dataError, promoJson, slug, currentLang]);

    const promoData = useMemo(() => {
        if (!promoJson?.comingSoonData || !slug) return null;

        const promoItem = promoJson.comingSoonData.find(item => item.slug === slug);
        
        if (!promoItem) return null;

        const translation = promoItem.translations?.[currentLang];
      
        const isActive = translation?.isActive === true;
        
        if (!isActive) return null;

        return {
            ...promoItem,
            startDateTimestamp: new Date(promoItem.startDate).getTime(),
            endDateTimestamp: new Date(promoItem.endDate).getTime(),
            ...(translation || {})
        };
    }, [promoJson, slug, currentLang]);

    const seoData = useMemo(() => {
        if (!promoData?.seo) return null;
        
        const seo = promoData.seo;
        const title = seo.title?.[currentLang] || seo.title?.en || promoData.campaignName || 'Акція | Carabinex';
        const description = seo.description?.[currentLang] || seo.description?.en || '';
        const keywords = seo.keywords?.[currentLang] || seo.keywords?.en || '';
        const type = seo.type || 'event';
        const robots = seo.robots || 'index, follow';
        
        const robotsParts = robots.split(', ');
        const noIndex = robotsParts.includes('noindex');
        const noFollow = robotsParts.includes('nofollow');
        
        return { title, description, keywords, type, noIndex, noFollow };
    }, [promoData, currentLang]);

    useEffect(() => {
        if (!promoData) return;

        const calculateTimeLeft = () => {
            const now = Date.now();
            if (now < promoData.startDateTimestamp) {
                return promoData.endDateTimestamp - promoData.startDateTimestamp;
            } else {
                return Math.max(promoData.endDateTimestamp - now, 0);
            }
        };

        setTimeLeft(calculateTimeLeft());

        const interval = setInterval(() => {
            setTimeLeft(calculateTimeLeft());
        }, 1000);

        return () => clearInterval(interval);
    }, [promoData]);

    const getTimeUnits = useCallback((ms) => {
        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return {
            days: {
                value: days,
                label: getTimeUnitLabel(days, 'days', currentLang)
            },
            hours: {
                value: hours,
                label: getTimeUnitLabel(hours, 'hours', currentLang)
            },
            minutes: {
                value: minutes,
                label: getTimeUnitLabel(minutes, 'minutes', currentLang)
            },
            seconds: {
                value: seconds,
                label: getTimeUnitLabel(seconds, 'seconds', currentLang)
            },
        };
    }, [currentLang]);

    const timeUnits = useMemo(() => getTimeUnits(timeLeft), [getTimeUnits, timeLeft]);

    const formatNumber = useCallback((num) => String(num).padStart(2, '0'), []);

    const isPromoFinished = useMemo(() => 
        Date.now() > (promoData?.endDateTimestamp || 0),
        [promoData]
    );

    const isPromoNotStarted = useMemo(() => 
        Date.now() < (promoData?.startDateTimestamp || 0),
        [promoData]
    );

    const heroColor = useMemo(() => 
        promoData?.promoPageInfo?.heroBlockColor || '#ffffff',
        [promoData]
    );

    const bannerColor = useMemo(() => 
        promoData?.promoPageInfo?.bannerBlockColor || '#1f252b',
        [promoData]
    );

    const colors = useMemo(() => ({
        accentColor: promoData?.promoPageInfo?.colors?.accentColor || heroColor,
        titleColor: promoData?.promoPageInfo?.colors?.titleColor || heroColor,
        subtitleColor: promoData?.promoPageInfo?.colors?.subtitleColor || heroColor,
        textColor: promoData?.promoPageInfo?.colors?.textColor || heroColor,
        timerAccentColor: promoData?.promoPageInfo?.colors?.timerAccentColor || heroColor,
        timerNumberColor: promoData?.promoPageInfo?.colors?.timerNumberColor || heroColor,
        timerLabelColor: promoData?.promoPageInfo?.colors?.timerLabelColor || heroColor,
        finishColor: promoData?.promoPageInfo?.colors?.finishColor || heroColor
    }), [promoData, heroColor]);

    if (isLoading) {
        return <PromoPageComponentSkeleton />;
    }

    if (hasError || !promoData) {
        return <ErrorPage hideHeaderFooterSetter={hideHeaderFooterSetter} />;
    }

    return (
        <>
            {seoData && (
                <SEO
                    title={seoData.title}
                    description={seoData.description}
                    keywords={seoData.keywords}
                    type={seoData.type}
                    noIndex={seoData.noIndex}
                    noFollow={seoData.noFollow}
                />
            )}
            
            <div className='promo__wrapper'>
                <div className='promo__hero'>
                    <div
                        className='promo__hero__cover'
                        style={{ backgroundImage: `url(${IMAGES_BASE_URL}${promoData.saleImage})` }}
                    >
                        <div className='promo__gradient'></div>
                        <div className='promo__hero__content'>
                            <p style={{ color: colors.accentColor }} className='promo__hero__content__accent'>
                                {promoData.campaignSubtitle}
                            </p>
                            <h2 style={{ color: colors.titleColor }} className='promo__hero__content__title'>
                                {promoData.campaignName}
                            </h2>
                            <p style={{ color: colors.subtitleColor }} className='promo__hero__content__subtitle'>
                                {promoData.promoPageInfo?.actionText}
                            </p>
                            <p style={{ color: colors.textColor }} className='promo__hero__content__text'>
                                {promoData.campaignPeriod
                                    ?.replace('{startDate}', promoData.startDate)
                                    ?.replace('{endDate}', promoData.endDate)}
                            </p>
                            <MoreButtonArticle />
                        </div>

                        <div className='promo__hero__timer'>
                            {!isPromoFinished ? (
                                <>
                                    <p style={{ color: colors.timerAccentColor }} className='promo__hero__timer__accent'>
                                        {isPromoNotStarted
                                            ? promoData.promoPageInfo?.startText
                                            : promoData.promoPageInfo?.endText
                                        }
                                    </p>
                                    <div className="promo__timer__numbers">
                                        <div style={{ color: colors.timerNumberColor }} className="numbers__item">{formatNumber(timeUnits.days.value)}</div>
                                        <div style={{ color: colors.timerNumberColor }} className="numbers__item">{formatNumber(timeUnits.hours.value)}</div>
                                        <div style={{ color: colors.timerNumberColor }} className="numbers__item">{formatNumber(timeUnits.minutes.value)}</div>
                                        <div style={{ color: colors.timerNumberColor }} className="numbers__item">{formatNumber(timeUnits.seconds.value)}</div>
                                    </div>
                                    <div className="promo__timer__labels">
                                        <div style={{ color: colors.timerLabelColor }} className="labels__item">{timeUnits.days.label}</div>
                                        <div style={{ color: colors.timerLabelColor }} className="labels__item">{timeUnits.hours.label}</div>
                                        <div style={{ color: colors.timerLabelColor }} className="labels__item">{timeUnits.minutes.label}</div>
                                        <div style={{ color: colors.timerLabelColor }} className="labels__item">{timeUnits.seconds.label}</div>
                                    </div>
                                </>
                            ) : (
                                <p style={{ color: colors.finishColor }} className='promo__hero__timer__finish'>
                                    {promoData.promoPageInfo?.finishPromo}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {!isPromoFinished && (
                    <>
                        <div className='promo__description'>
                            <div className="promo__description__left">
                                {(promoData.promoPageInfo?.descriptionTitle ||
                                    promoData.promoPageInfo?.descriptionSubtitle ||
                                    promoData.promoPageInfo?.buttonText) && (
                                        <div className="promo__description__left__content">
                                            {promoData.promoPageInfo?.descriptionTitle && (
                                                <h4 className="promo__description__left__content__title">
                                                    {promoData.promoPageInfo.descriptionTitle}
                                                </h4>
                                            )}
                                            {promoData.promoPageInfo?.descriptionSubtitle && (
                                                <h5 className="promo__description__left__content__subtitle">
                                                    {promoData.promoPageInfo.descriptionSubtitle}
                                                </h5>
                                            )}
                                            {promoData.promoPageInfo?.buttonText && promoData.promoPageInfo?.buttonLink && (
                                                <Link
                                                    to={`form-general?promocode=${promoData.promoPageInfo.mainPromocode}`}
                                                    className="promo__description__left__content__button"
                                                >
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                    {promoData.promoPageInfo.buttonText}
                                                </Link>
                                            )}
                                        </div>
                                    )}
                            </div>

                            <div className="promo__description__right">
                                {promoData.promoPageInfo?.descriptionText1 && (
                                    <p className="promo__description__right__text">{promoData.promoPageInfo.descriptionText1}</p>
                                )}
                                {promoData.promoPageInfo?.descriptionText2 && (
                                    <p className="promo__description__right__text">{promoData.promoPageInfo.descriptionText2}</p>
                                )}
                                {promoData.promoPageInfo?.descriptionText3 && (
                                    <p className="promo__description__right__text">{promoData.promoPageInfo.descriptionText3}</p>
                                )}
                            </div>
                        </div>
                        
                        <Discount slug={promoData.slug} mainPromocode={promoData.promoPageInfo?.mainPromocode} />

                        <div className='promo__steps'>
                            <h4 className='promo__steps__title'>{promoData.promoPageInfo?.stepsTitle}</h4>
                            <p className="promo__steps__item__subtitle">
                                {promoData.promoPageInfo?.stepsSubtitle}
                            </p>
                            <div className='promo__steps__item__wrapper'>
                                {promoData.promoPageInfo?.steps?.map((step) => (
                                    <div key={step.id} className='promo__steps__item'>
                                        <img src={`${IMAGES_BASE_URL}${step.stepIcon}`} alt={step.stepTitle} className="steps__icon" loading="lazy"/>
                                        <h5 className='promo__steps__item__title'>{step.stepTitle}</h5>
                                        <p className='promo__steps__item__text'>{step.stepSubtitle}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className='promo__banner__wrapper'>
                            <div style={{ backgroundColor: bannerColor }} className='promo__banner'>
                                <img src={`${IMAGES_BASE_URL}${promoData.bannerPattern}`} alt={promoData.promoPageInfo?.buttonText || 'Promo banner'} className="banner__pattern" loading="lazy"/>
                                <h2 className='promo__banner__title'>{promoData.promoPageInfo?.bannerTitle}</h2>
                                <h3 className='promo__banner__subtitle'>{promoData.promoPageInfo?.bannerSubtitle}</h3>
                                <Link to={`form-general?promocode=${promoData.promoPageInfo?.mainPromocode}`} className='promo__banner__button'>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    {promoData.promoPageInfo?.buttonText}
                                </Link>
                            </div>
                        </div>
                        <FAQs page='promoPage' />
                    </>
                )}
            </div>
        </>
    );
};

export default React.memo(PromoPageComponent);